import React from 'react';
import './app.css';
import { createBrowserRouter, Navigate, RouterProvider, Outlet } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { theme, raphaTheme } from './theme';
import { isRaphaDomain } from './services/subdomain';

import { Homepage } from './Homepage';
import { About } from './About';
import Analytics from './Analytics';
import Register from './Register';
import Verify from './Verify';
import Login from './Login';
import PasswordResetRequest from './PasswordResetRequest';
import ResetPassword from './ResetPassword';
import Account from './Account';
import PrivacyZones from './PrivacyZones';
import { Dashboard } from './Dashboard';
import SessionFocus from './SessionFocus';
import RaphaRegister from './rapha/RaphaRegister';
import RaphaWelcome from './rapha/RaphaWelcome';
import RaphaFit from './rapha/RaphaFit';
import RaphaCatalog from './rapha/RaphaCatalog';
import MagicLinkLogin from './rapha/MagicLinkLogin';
import RaphaGuard from './components/RaphaGuard';
import IdentityGuard from './components/IdentityGuard';
import LiabilityGuard from './components/LiabilityGuard';
import FreeTierBanner from './components/FreeTierBanner';
import CookieBanner from './components/CookieBanner';
import Subscription from './Subscription';
import SubscriptionStubSuccess from './SubscriptionStubSuccess';
import SharedActivity from './SharedActivity';
import SessionRouter from './SessionRouter';
import ImportPage from './Import';
import Drops from './Drops';
import PeakDropPage from './PeakDropPage';
import WindEmbed from './WindEmbed';
import Wake from './Wake';
import WakeEmbed from './WakeEmbed';
import ShareCardEmbed from './ShareCardEmbed';
import { currentFeatures } from './services/features';
import Feed from './Feed';
import AlertsPage from './AlertsPage';
import Discover from './Discover';
import UserProfile from './UserProfile';
import Integrations from './Integrations';
import Admin from './Admin';
import ImpersonationBanner from './components/ImpersonationBanner';
import Privacy from './Privacy';
import Terms from './Terms';
import Liability from './Liability';
import RequireAuth from './components/RequireAuth';
import Dropped from './Dropped';
import ReplayView from './ReplayView';
import PositionDetail from './PositionDetail';

const activeTheme = isRaphaDomain() ? raphaTheme : theme;

// Track 1 — layout route that gates every child route with RaphaGuard.
// On non-rapha subdomains the guard is a no-op pass-through; on rapha.
// aero.chat it routes unauthenticated users to /login and renders the
// "Rapha Members Only" landing for authenticated non-rapha users.
//
// Wave 19.1 — Also mounts the FreeTierBanner above every authenticated
// page on aero.chat. The banner self-hides on rapha.aero.chat, for
// role='rapha' users, and for non-free plans, so it only appears when
// the rider needs to see their remaining weekly time.
function RaphaGuardLayout() {
  return (
    <RaphaGuard>
      {/* Wave 20.24 — Liability accept comes BEFORE identity setup.
          Legal agreement is a hard requirement; picking a username is a
          nice-to-have. Nesting Identity inside Liability also means only
          one modal is ever open at a time — the inner guard's modal is
          never even mounted while the outer one is blocking. */}
      <LiabilityGuard>
        <IdentityGuard>
          <ImpersonationBanner />
          <FreeTierBanner />
          <Outlet />
          <CookieBanner />
        </IdentityGuard>
      </LiabilityGuard>
    </RaphaGuard>
  );
}

// Wave 20.37 — capability-gated route entries. If the current tier
// doesn't include the capability, the path 404s → /feed instead of
// serving a page that would show empty / disabled content. Read from
// currentFeatures() at module load (subdomain doesn't change within
// a session).
const F = currentFeatures();
const gate = (allowed: boolean, page: React.ReactElement): React.ReactElement =>
  allowed ? page : <Navigate to="/feed" replace />;

const router = createBrowserRouter([
  // Wave 20.33 — /embed/wind is a bare canvas page for the mobile
  // WebView overlay. Sits OUTSIDE the RaphaGuardLayout so it has no
  // banners, cookie bar, liability modal, etc. The page is loaded
  // inside a transparent WebView on top of the mobile MapView.
  { path: '/embed/wind', element: <WindEmbed /> },
  // Wave 20.47 — public replay share. No login required.
  { path: '/replay/:token', element: <ReplayView /> },
  // Wave 20.43 — /wake-embed is a bare canvas page for the Aero
  // Wake animation. Same pattern as /embed/wind — sits outside
  // RaphaGuardLayout so it has no chrome; iframed by /wake on web
  // and WebView-loaded by the mobile WakeTab. Parameter-driven,
  // no data access.
  { path: '/wake-embed', element: <WakeEmbed /> },
  // Wave 20.49cs — /share-card-embed renders a session's share card
  // (map + wind still + peak pins + stats) and posts the image back
  // to the host. Loaded in a WebView by the mobile app so both
  // platforms ship the same renderer. Auth via ?jwt= like /embed/wind.
  { path: '/share-card-embed', element: <ShareCardEmbed /> },
  {
    element: <RaphaGuardLayout />,
    children: [
      { path: '/', element: <Homepage /> },
      { path: '/about', element: <About theme={activeTheme} /> },
      { path: '/register', element: <Register /> },
      { path: '/login', element: <Login /> },
      { path: '/password/reset/request', element: <PasswordResetRequest /> },
      { path: '/password/reset', element: <ResetPassword /> },
      { path: '/verify/email', element: <Verify /> },
      // Wave 20.49h — every route below that meaningfully needs a
      // signed-in user is wrapped in <RequireAuth>. Logged-out
      // visitors bounce straight to /login?next=<path> — no more
      // "please sign in" pages half-rendering under a protected URL.
      // Public routes stay unwrapped: homepage, about, register/login,
      // password reset, verify email, /privacy, /terms, /liability, /wake-embed,
      // magic-link,
      // and the public drop pages (/shared, /peak, /dropped, /u/:username).
      { path: '/session', element: <RequireAuth><Dashboard /></RequireAuth> },
      // Wave 20.44 — pre-session picker: which position are we
      // working on. Feeds a focusClass through into /session.
      { path: '/session/focus', element: <RequireAuth><SessionFocus /></RequireAuth> },
      // Wave 20.47g — per-position drill-in from the aero pane.
      { path: '/positions/:class', element: <RequireAuth><PositionDetail /></RequireAuth> },
      // Wave 20.43 — Aero Wake landing screen.
      { path: '/wake', element: <RequireAuth>{gate(F.wake, <Wake />)}</RequireAuth> },
      // Wave 20.26 — legacy /ai bookmarks / JWT-issued redirects.
      { path: '/ai', element: <Navigate to="/session" replace /> },
      { path: '/account', element: <RequireAuth><Account /></RequireAuth> },
      { path: '/privacy-zones', element: <RequireAuth><PrivacyZones /></RequireAuth> },
      // Wave 14 — unified analytics dashboard replaces the old
      // /sessions list and /positions/analytics. /sessions/:id (deep
      // dive) stays. /positions (lightweight reference) stays.
      // Wave 20.14 — /dashboard becomes the unified feed. /dashboard/aero
      // keeps the legacy Analytics view for the CdA-heavy audience.
      { path: '/feed', element: <RequireAuth><Feed /></RequireAuth> },
      { path: '/alerts', element: <RequireAuth><AlertsPage /></RequireAuth> },
      { path: '/dashboard', element: <Navigate to="/feed" replace /> },
      { path: '/dashboard/aero', element: <RequireAuth><Analytics /></RequireAuth> },
      { path: '/discover', element: <RequireAuth>{gate(F.discoverPeople, <Discover />)}</RequireAuth> },
      // /u/:username is a public profile — no auth required.
      { path: '/u/:username', element: gate(F.otherUserProfiles, <UserProfile />) },
      { path: '/integrations', element: <RequireAuth><Integrations /></RequireAuth> },
      { path: '/admin', element: <RequireAuth><Admin /></RequireAuth> },
      { path: '/privacy', element: <Privacy /> },
      // Wave 20.64 — public Terms of Service, same public treatment as /privacy.
      { path: '/terms', element: <Terms /> },
      // Wave 20.65 — public Liability Release. The document with recorded
      // consent had no page; the accept modal was the only place it existed.
      { path: '/liability', element: <Liability /> },
      // Wave 20.40 — unified session-detail URL. Both indoor
      // (SessionDetail) and outdoor (ActivityDetail) live under
      // /sessions/:id; the SessionRouter fetches session.type and
      // renders the appropriate view. /activities/:id kept as an
      // alias so drops + bookmarks in the wild keep working.
      { path: '/sessions/:id', element: <RequireAuth><SessionRouter /></RequireAuth> },
      { path: '/activities/:id', element: <RequireAuth>{gate(F.outdoorSessions, <SessionRouter />)}</RequireAuth> },
      { path: '/import', element: <RequireAuth>{gate(F.outdoorSessions, <ImportPage />)}</RequireAuth> },
      { path: '/drops', element: <RequireAuth>{gate(F.drops, <Drops />)}</RequireAuth> },
      // Wave 20.22 — unified /dropped/:token URL. Legacy /peak/ and
      // /shared/ paths kept for links already in the wild.
      { path: '/dropped/:token', element: gate(F.drops, <Dropped />) },
      { path: '/peak/:token', element: gate(F.drops, <PeakDropPage />) },
      // Wave 20 — public Drop Link page (no auth). RaphaGuard's public
      // allowlist explicitly permits /shared/*.
      { path: '/shared/:token', element: gate(F.drops, <SharedActivity />) },
      { path: '/rapha/register', element: <RaphaRegister /> },
      { path: '/rapha/welcome', element: <RaphaWelcome /> },
      { path: '/rapha/fit', element: <RequireAuth><RaphaFit /></RequireAuth> },
      { path: '/rapha/catalog', element: <RequireAuth><RaphaCatalog /></RequireAuth> },
      { path: '/login/magic', element: <MagicLinkLogin /> },
      { path: '/subscription', element: <RequireAuth><Subscription /></RequireAuth> },
      { path: '/subscription/stub-success', element: <RequireAuth><SubscriptionStubSuccess /></RequireAuth> },
    ],
  },
]);

function App() {
  return (
    <ThemeProvider theme={activeTheme}>
      <CssBaseline />
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}

export default App;
